/**
 * Shared Utility Functions
 * Centralized utilities used across all components
 */

import { clsx } from "clsx";
import { twMerge } from "tailwind-merge";

// ===== SHADCN UI UTILITY =====

/**
 * Merge Tailwind CSS classes (used by shadcn/ui components)
 */
export function cn(...inputs) {
  return twMerge(clsx(inputs));
}


// ===== CURRENCY FORMATTING =====

/**
 * Format a number as Indian Rupee currency (lakhs/crores style: 1,00,000)
 * @param {number} amount - The amount to format
 * @param {boolean} showSymbol - Whether to show ₹ symbol (default: true)
 * @returns {string} Formatted currency string (no decimals, Indian comma style)
 */
export const formatCurrency = (amount, showSymbol = true) => {
  const num = Math.round(Number(amount) || 0);
  
  // Use Indian number system (lakhs/crores)
  const formatted = new Intl.NumberFormat('en-IN', {
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
    useGrouping: true
  }).format(Math.abs(num));
  
  const sign = num < 0 ? '-' : '';
  return showSymbol ? `${sign}₹${formatted}` : `${sign}${formatted}`;
};

/**
 * Format currency with sign indicator (+/-)
 * @param {number} amount - The amount to format
 * @param {boolean} forceSign - Always show + for positive (default: false)
 */
export const formatCurrencyWithSign = (amount, forceSign = false) => {
  const num = Math.round(Number(amount) || 0);
  const formatted = formatCurrency(Math.abs(num));
  if (num > 0 && forceSign) return `+${formatted}`;
  if (num < 0) return `-${formatted}`;
  return formatted;
};

/**
 * Format large numbers in compact form (1.5L, 2.3Cr) - no decimals for small amounts
 */
export const formatCompactCurrency = (amount) => {
  const num = Math.round(Number(amount) || 0);
  const absNum = Math.abs(num);
  
  if (absNum >= 10000000) {
    return `₹${Math.round(num / 10000000)}Cr`;
  } else if (absNum >= 100000) {
    return `₹${Math.round(num / 100000)}L`;
  } else if (absNum >= 1000) {
    return `₹${Math.round(num / 1000)}K`;
  }
  return formatCurrency(num);
};

/**
 * Format number with Indian grouping (no currency symbol)
 * @param {number} num - The number to format
 * @returns {string} Formatted number string (Indian comma style, no decimals)
 */
export const formatNumber = (num) => {
  return formatCurrency(num, false);
};


// ===== DATE FORMATTING =====

/**
 * Format date for display (DD-MM-YYYY)
 */
export const formatDate = (dateStr) => {
  if (!dateStr) return '-';
  try {
    const date = new Date(dateStr);
    return date.toLocaleDateString('en-IN', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric'
    });
  } catch {
    return dateStr;
  }
};

/**
 * Format date with time
 */
export const formatDateTime = (dateStr) => {
  if (!dateStr) return '-';
  try {
    const date = new Date(dateStr);
    return date.toLocaleString('en-IN', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  } catch {
    return dateStr;
  }
};

/**
 * Get relative time (e.g., "2 hours ago")
 */
export const formatRelativeTime = (dateStr) => {
  if (!dateStr) return '-';
  try {
    const date = new Date(dateStr);
    const now = new Date();
    const diffMs = now - date;
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);
    
    if (diffMins < 1) return 'Just now';
    if (diffMins < 60) return `${diffMins}m ago`;
    if (diffHours < 24) return `${diffHours}h ago`;
    if (diffDays < 7) return `${diffDays}d ago`;
    return formatDate(dateStr);
  } catch {
    return dateStr;
  }
};

/**
 * Get current date in YYYY-MM-DD format
 */
export const getTodayDate = () => {
  return new Date().toISOString().split('T')[0];
};

/**
 * Get current month in YYYY-MM format
 */
export const getCurrentMonth = () => {
  return new Date().toISOString().slice(0, 7);
};


// ===== STRING UTILITIES =====

/**
 * Truncate text with ellipsis
 */
export const truncateText = (text, maxLength = 50) => {
  if (!text) return '';
  if (text.length <= maxLength) return text;
  return text.substring(0, maxLength - 3) + '...';
};

/**
 * Capitalize first letter
 */
export const capitalize = (str) => {
  if (!str) return '';
  return str.charAt(0).toUpperCase() + str.slice(1).toLowerCase();
};

/**
 * Convert to title case
 */
export const toTitleCase = (str) => {
  if (!str) return '';
  return str.split(' ').map(word => capitalize(word)).join(' ');
};


// ===== VALIDATION UTILITIES =====

/**
 * Check if value is a valid number
 */
export const isValidNumber = (value) => {
  return !isNaN(parseFloat(value)) && isFinite(value);
};

/**
 * Parse number safely (returns 0 for invalid)
 */
export const safeParseFloat = (value, defaultValue = 0) => {
  const parsed = parseFloat(value);
  return isNaN(parsed) ? defaultValue : parsed;
};

/**
 * Validate email format
 */
export const isValidEmail = (email) => {
  return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);
};

/**
 * Validate phone number (10 digits)
 */
export const isValidPhone = (phone) => {
  return /^\d{10}$/.test(phone?.replace(/\D/g, ''));
};


// ===== STATUS/BADGE UTILITIES =====

/**
 * Get status badge color class
 */
export const getStatusColor = (status) => {
  const colors = {
    // Common statuses
    'approved': 'bg-green-100 text-green-800 border-green-300',
    'completed': 'bg-green-100 text-green-800 border-green-300',
    'paid': 'bg-green-100 text-green-800 border-green-300',
    'active': 'bg-green-100 text-green-800 border-green-300',
    
    'pending': 'bg-yellow-100 text-yellow-800 border-yellow-300',
    'processing': 'bg-yellow-100 text-yellow-800 border-yellow-300',
    'partially_paid': 'bg-yellow-100 text-yellow-800 border-yellow-300',
    
    'closed': 'bg-blue-100 text-blue-800 border-blue-300',
    'reviewed': 'bg-blue-100 text-blue-800 border-blue-300',
    
    'draft': 'bg-gray-100 text-gray-600 border-gray-300',
    'inactive': 'bg-gray-100 text-gray-600 border-gray-300',
    
    'rejected': 'bg-red-100 text-red-800 border-red-300',
    'failed': 'bg-red-100 text-red-800 border-red-300',
    'overdue': 'bg-red-100 text-red-800 border-red-300',
    'terminated': 'bg-red-100 text-red-800 border-red-300',
  };
  
  return colors[status?.toLowerCase()] || 'bg-gray-100 text-gray-600 border-gray-300';
};

/**
 * Get priority badge color
 */
export const getPriorityColor = (priority) => {
  const colors = {
    1: 'bg-red-100 text-red-800',      // High
    2: 'bg-orange-100 text-orange-800', // Medium
    3: 'bg-green-100 text-green-800',   // Low
  };
  return colors[priority] || 'bg-gray-100 text-gray-600';
};

/**
 * Get priority label
 */
export const getPriorityLabel = (priority) => {
  const labels = { 1: 'High', 2: 'Medium', 3: 'Low' };
  return labels[priority] || 'Unknown';
};


// ===== ARRAY/OBJECT UTILITIES =====

/**
 * Group array by key
 */
export const groupBy = (array, key) => {
  return array.reduce((result, item) => {
    const groupKey = typeof key === 'function' ? key(item) : item[key];
    (result[groupKey] = result[groupKey] || []).push(item);
    return result;
  }, {});
};

/**
 * Sort array by key
 */
export const sortBy = (array, key, ascending = true) => {
  return [...array].sort((a, b) => {
    const aVal = typeof key === 'function' ? key(a) : a[key];
    const bVal = typeof key === 'function' ? key(b) : b[key];
    const comparison = aVal < bVal ? -1 : aVal > bVal ? 1 : 0;
    return ascending ? comparison : -comparison;
  });
};

/**
 * Remove duplicates from array
 */
export const uniqueBy = (array, key) => {
  const seen = new Set();
  return array.filter(item => {
    const val = typeof key === 'function' ? key(item) : item[key];
    if (seen.has(val)) return false;
    seen.add(val);
    return true;
  });
};

/**
 * Deep clone object
 */
export const deepClone = (obj) => {
  return JSON.parse(JSON.stringify(obj));
};


// ===== ERROR HANDLING =====

/**
 * Extract error message from various error formats
 */
export const getErrorMessage = (error, fallback = 'An error occurred') => {
  if (typeof error === 'string') return error;
  if (error?.response?.data?.detail) return error.response.data.detail;
  if (error?.response?.data?.message) return error.response.data.message;
  if (error?.message) return error.message;
  return fallback;
};


// ===== DEBOUNCE/THROTTLE =====

/**
 * Debounce function calls
 */
export const debounce = (func, wait = 300) => {
  let timeout;
  return (...args) => {
    clearTimeout(timeout);
    timeout = setTimeout(() => func(...args), wait);
  };
};

/**
 * Throttle function calls
 */
export const throttle = (func, limit = 300) => {
  let inThrottle;
  return (...args) => {
    if (!inThrottle) {
      func(...args);
      inThrottle = true;
      setTimeout(() => inThrottle = false, limit);
    }
  };
};


// ===== LOCAL STORAGE HELPERS =====

/**
 * Safe localStorage get with JSON parse
 */
export const getStoredValue = (key, defaultValue = null) => {
  try {
    const item = localStorage.getItem(key);
    return item ? JSON.parse(item) : defaultValue;
  } catch {
    return defaultValue;
  }
};

/**
 * Safe localStorage set with JSON stringify
 */
export const setStoredValue = (key, value) => {
  try {
    localStorage.setItem(key, JSON.stringify(value));
    return true;
  } catch {
    return false;
  }
};
